import moment from 'moment';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';

import ImageComponent from 'shared/ImageComponent';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
/**
 * Component for rendering an individual comment with user info, content, and action buttons
 *
 * @param {Object} props - The component props
 * @param {Object} props.comment - The comment object containing id, text, user info, and time
 * @param {Function} props.onEdit - Function to call when edit button is clicked
 * @param {Function} props.onDelete - Function to call when delete button is clicked
 * @param {boolean} props.isUpdatingComment - Whether a comment is currently being updated
 * @param {string|null} props.deletingCommentId - ID of the comment currently being deleted (if any)
 * @param {string|null} props.editingComment - ID of the comment currently being edited (if any)
 * @param {string} props.editCommentText - Current text in the edit field
 * @param {Function} props.onSave - Function to call when save button is clicked
 * @param {Function} props.onCancel - Function to call when cancel button is clicked
 * @param {Function} props.setEditCommentText - Function to update the edit text state
 * @returns {JSX.Element} The rendered comment item
 */
export const CommentItem = ({
  comment,
  onEdit,
  onDelete,
  isUpdatingComment,
  deletingCommentId,
  editingComment,
  editCommentText,
  onSave,
  onCancel,
  setEditCommentText,
}) => {
  const isDeleting = deletingCommentId === comment.id;
  return (
    <div className="d-flex">
      <div>
        <ImageComponent
          filePath={comment.user.avatar}
          fileName={comment.user.name}
          className="me-2"
          avatarSize="small"
          style={{
            backgroundColor: '#2196F3',
            color: '#ffffff',
          }}
        />
      </div>
      <div className="flex-grow-1">
        <div className="d-flex justify-content-between align-items-center">
          <div className="text-bold F-size14">{comment.user.name}</div>
          {comment.editable && (
            <div>
              <i
                className={`pi pi-pencil pointer me-2 ${editingComment === comment.id ? 'text-primary' : ''}`}
                onClick={() => !isDeleting && onEdit(comment.id, comment.text)}
                style={{ opacity: isDeleting ? 0.5 : 1 }}
              ></i>
              {isDeleting ? (
                <i className="pi pi-spin pi-spinner text-muted"></i>
              ) : (
                <i className="pi pi-trash pointer text-danger" onClick={() => onDelete(comment.id)}></i>
              )}
            </div>
          )}
        </div>
        <div className="F-size12 text-muted">{moment(comment.time).format('MM/DD/YYYY hh:mm A')}</div>
        {editingComment === comment.id ? (
          <div className="mt-2">
            <TextSnippetWrapper
              type="textarea"
              rows="3"
              className="w-100 input-shadow"
              value={editCommentText}
              onChange={(e) => setEditCommentText(e.target.value)}
            />
            <div className="d-flex justify-content-end mt-2">
              <Button type="reset" className="me-2 btn p-button-secondary outline" onClick={onCancel} disabled={isUpdatingComment}>
                Cancel
              </Button>
              <Button
                className="p-button-secondary me-2"
                type="submit"
                onClick={() => onSave(comment.id)}
                disabled={isUpdatingComment}
                loading={isUpdatingComment}
                loadingIcon="pi pi-spin pi-spinner"
                style={{ minWidth: '65px' }}
              >
                update
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="F-size14 comment-text-truncated"
            data-pr-tooltip={comment.text.length > 150 ? comment.text : null}
            data-pr-position="top"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {comment.text}
          </div>
        )}
        <Tooltip target=".comment-text-truncated" />
      </div>
    </div>
  );
};
